import { Button, Card } from "@etain/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, setCsrfToken } from "../lib/api.js";
import { normalizeSession } from "../session.js";
import { microsoftPasswordFallback, rememberMicrosoftReturn } from "../lib/auth-return-target.js";
import { ReturnAfterLogin } from "./ReturnAfterLogin.js";

export function MicrosoftSignIn({
  destination,
  targetTenantId,
}: {
  destination: string;
  targetTenantId?: string;
}) {
  const [search] = useSearchParams();
  const providers = useQuery({
    queryKey: ["auth-providers"],
    queryFn: () => api.get<{ microsoft: boolean }>("/auth/providers"),
    staleTime: 0,
    refetchInterval: 15000,
    retry: false,
  });
  const start = useMutation({
    mutationFn: (selectAccount: boolean) =>
      api.post<{ authorizationUrl: string }>("/auth/microsoft/start", {
        returnTo: destination,
        ...(targetTenantId ? { targetTenantId } : {}),
        ...(selectAccount ? { selectAccount: true } : {}),
      }),
    onSuccess: (result) => {
      rememberMicrosoftReturn({
        target: destination,
        ...(targetTenantId ? { tenantId: targetTenantId } : {}),
      });
      window.location.assign(result.authorizationUrl);
    },
    onError: () => {
      void providers.refetch();
    },
  });
  return (
    <>
      {!providers.isError && providers.data?.microsoft === true ? (
        <div className="login-form microsoft-sign-in">
          <Button variant="secondary" busy={start.isPending} onClick={() => start.mutate(false)}>
            Mit Microsoft anmelden
          </Button>
          {["tenant", "unlinked"].includes(search.get("sso") ?? "") ? (
            <Button variant="quiet" busy={start.isPending} onClick={() => start.mutate(true)}>
              Anderes Microsoft-Konto verwenden
            </Button>
          ) : null}
          <p className="text-muted">oder mit E-Mail und Passwort</p>
        </div>
      ) : null}
      {start.isError ? <p role="alert">{start.error.message}</p> : null}
    </>
  );
}

export function MicrosoftLoginFeedback() {
  const [search] = useSearchParams();
  const messages: Record<string, string> = {
    disabled:
      "Die Microsoft-Anmeldung ist für diesen Mandanten deaktiviert. Bitte melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an.",
    unlinked:
      "Dieses Microsoft-Konto ist noch nicht mit Ihrem Hub-Zugang verbunden. Melden Sie sich einmal mit E-Mail und Passwort an und verbinden Sie Microsoft in Ihrem Profil.",
    tenant:
      "Dieses Microsoft-Konto ist für den gewählten Mandanten nicht freigegeben. Bitte verwenden Sie ein anderes Microsoft-Konto oder E-Mail und Passwort.",
    failed:
      "Die Microsoft-Anmeldung konnte nicht sicher abgeschlossen werden. Bitte starten Sie erneut oder verwenden Sie E-Mail und Passwort.",
  };
  const message = messages[search.get("sso") ?? ""];
  if (!message) return null;
  return (
    <div className="form-alert" role="status">
      <span>{message}</span>
      <a href="#email" onClick={() => document.getElementById("email")?.focus()}>
        Mit E-Mail und Passwort anmelden
      </a>
    </div>
  );
}

export function MicrosoftSignInCompletePage() {
  const [search] = useSearchParams();
  const id = search.get("anmeldung") ?? "";
  const [destination, setDestination] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const submitted = useRef(false);
  const pending = useQuery({
    queryKey: ["microsoft-pending", id],
    queryFn: () =>
      api.get<{ tenants: { id: string; name: string }[] }>(
        `/auth/microsoft/pending/${encodeURIComponent(id)}`,
      ),
    enabled: !!id,
    retry: false,
  });
  const finish = useMutation({
    mutationFn: (tenantId: string) =>
      api.post<{ session: unknown; returnTo: string }>("/auth/microsoft/complete", {
        transactionId: id,
        tenantId,
      }),
    onSuccess: (result) => {
      const session = normalizeSession(result.session);
      setCsrfToken(session.csrfToken);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
      queryClient.setQueryData(["session"], session);
      setDestination(result.returnTo);
    },
  });
  useEffect(() => {
    if (pending.data?.tenants.length === 1 && !submitted.current) {
      submitted.current = true;
      finish.mutate(pending.data.tenants[0]!.id);
    }
  }, [pending.data, finish]);
  if (destination !== null) return <ReturnAfterLogin target={destination} />;
  const error = pending.error ?? finish.error;
  return (
    <main className="login-page">
      <section className="login-form-wrap">
        <Card className="login-card">
          <h1>
            {pending.data && pending.data.tenants.length > 1
              ? "Mandant auswählen"
              : "Microsoft-Anmeldung"}
          </h1>
          {error ? (
            <p role="alert">{error.message}</p>
          ) : (
            <p role="status">
              {pending.data && pending.data.tenants.length > 1
                ? "Wählen Sie Ihren Mandanten."
                : "Ihre Anmeldung wird abgeschlossen …"}
            </p>
          )}
          {pending.data && pending.data.tenants.length > 1
            ? pending.data.tenants.map((tenant) => (
                <Button
                  key={tenant.id}
                  busy={finish.isPending}
                  onClick={() => finish.mutate(tenant.id)}
                >
                  {tenant.name}
                </Button>
              ))
            : null}
          <Link to={microsoftPasswordFallback()}>Zur Anmeldung mit E-Mail und Passwort</Link>
        </Card>
      </section>
    </main>
  );
}
